import React, { useState, useEffect } from "react";
import "../styles/SelfAssessmentPage.css";
import Button from "../common/Button";
import { Outlet } from "react-router-dom";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { FaArrowLeft } from "react-icons/fa";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;

// Map levels to numbers
const levelOrder = {
  Beginner: 1,
  Developing: 2,
  Proficient: 3,
  Advanced: 4,
  Mastery: 5,
};

// Reverse map for numbers to levels
const reverseLevelOrder = {
  1: "Beginner",
  2: "Developing",
  3: "Proficient",
  4: "Advanced",
  5: "Mastery",
};

const SUBJECT_PRIORITY = ["math", "science", "physics", "chemistry", "biology"];

const getSubjectCategory = (value) => {
  if (typeof value !== "string") return "";
  const normalized = value.toLowerCase();
  if (normalized.includes("math")) return "math";
  if (normalized.includes("physics")) return "physics";
  if (normalized.includes("science")) return "science";
  if (normalized.includes("chemistry")) return "chemistry";
  if (normalized.includes("bio")) return "biology";
  return "other";
};

const getSubjectRank = (value) => {
  const category = getSubjectCategory(value);
  const index = SUBJECT_PRIORITY.indexOf(category);
  return index === -1 ? SUBJECT_PRIORITY.length : index;
};

// Calculate topic level based on subtopic levels using AGGREGATE SCORE LOGIC
// The topic level is determined by calculating the average of all subtopic levels
// and rounding to the nearest level (Beginner=1, Developing=2, Proficient=3, Advanced=4, Mastery=5)
const calculateTopicLevel = (subtopics) => {
  if (!subtopics || Object.keys(subtopics).length === 0) return null;
  const validLevels = Object.values(subtopics)
    .map((subtopic) => levelOrder[subtopic.level])
    .filter((level) => level !== undefined);
  if (validLevels.length === 0) return null;
  const avgLevel =
    validLevels.reduce((sum, level) => sum + level, 0) / validLevels.length;
  const calculatedLevel = Math.round(avgLevel); // Use Math.round instead of Math.ceil
  return reverseLevelOrder[Math.min(calculatedLevel, 5)]; // Cap at Mastery
};

const cloneLevels = (levels = {}) => {
  const cloned = {};
  Object.entries(levels || {}).forEach(([subject, topics]) => {
    cloned[subject] = {};
    Object.entries(topics || {}).forEach(([topic, topicData]) => {
      cloned[subject][topic] = {
        level: topicData?.level ?? null,
        lastUpdated: topicData?.lastUpdated ?? null,
        subtopics: {},
      };
      Object.entries(topicData?.subtopics || {}).forEach(
        ([subtopicName, subtopicData]) => {
          cloned[subject][topic].subtopics[subtopicName] = {
            level: subtopicData?.level ?? null,
            lastUpdated: subtopicData?.lastUpdated ?? null,
          };
        }
      );
    });
  });
  return cloned;
};

const mergeLevels = (baseLevels = {}, overrideLevels = {}) => {
  const merged = cloneLevels(baseLevels);

  Object.entries(overrideLevels || {}).forEach(([subject, topics]) => {
    if (!merged[subject]) {
      merged[subject] = {};
    }

    Object.entries(topics || {}).forEach(([topic, topicData]) => {
      const existingTopic = merged[subject][topic] || {
        level: null,
        lastUpdated: null,
        subtopics: {},
      };

      const mergedSubtopics = { ...existingTopic.subtopics };
      Object.entries(topicData?.subtopics || {}).forEach(
        ([subtopicName, subtopicData]) => {
          mergedSubtopics[subtopicName] = {
            level:
              subtopicData?.level ??
              mergedSubtopics[subtopicName]?.level ??
              null,
            lastUpdated:
              subtopicData?.lastUpdated ??
              mergedSubtopics[subtopicName]?.lastUpdated ??
              null,
          };
        }
      );

      merged[subject][topic] = {
        level: topicData?.level ?? existingTopic.level ?? null,
        lastUpdated:
          topicData?.lastUpdated ?? existingTopic.lastUpdated ?? null,
        subtopics: mergedSubtopics,
      };
    });
  });

  return merged;
};

export const SelfAssessmentPage = () => {
  const [subjectLevels, setSubjectLevels] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("self-assessment");
  const [selectedSubject, setSelectedSubject] = useState("");
  const [subjectList, setSubjectList] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();

  // Function to handle back navigation
  const handleBackClick = () => {
    setSubjectLevels({});
    // Check if there's a previous page in history
    if (window.history.length > 1) {
      navigate(-1);
    } else {
      // Fallback to home page if no history
      navigate("/");
    }
  };

  // Function to handle tab navigation
  const handleTabClick = (tab) => {
    if (tab === "evaluate-answer-sheets") {
      navigate("/practice/evaluateAnswerSheet");
    } else if (tab === "take-challenge") {
      navigate("/practice");
    }
    // For self-assessment tab, we stay on the same page
  };

  // Local state for syllabus data
  const [syllabusData, setSyllabusData] = useState([]);
  const [isLoadingSyllabus, setIsLoadingSyllabus] = useState(true);
  const [syllabusError, setSyllabusError] = useState(null);

  // Fetch syllabus data
  useEffect(() => {
    const fetchSyllabus = async () => {
      setIsLoadingSyllabus(true);
      setSyllabusError(null);
      try {
        const response = await axios.get(`${baseUrl}/api/v2/syllabus`, {});
        setSyllabusData(response.data || []);
        const initialLevels = {};

        // Process the syllabus data array
        const sortedSyllabus = response.data.sort((a, b) => {
          const rankDifference =
            getSubjectRank(a.subject) - getSubjectRank(b.subject);
          if (rankDifference !== 0) return rankDifference;
          return (a?.subject || "").localeCompare(b?.subject || "");
        });
        const subjectsFromSyllabus = sortedSyllabus.map(
          (syllabusItem) => syllabusItem.subject
        );

        sortedSyllabus.forEach((syllabusItem) => {
          const subject = syllabusItem.subject;
          initialLevels[subject] = {};
          // Process chapters/topics for this subject
          if (syllabusItem.chapters && Array.isArray(syllabusItem.chapters)) {
            syllabusItem.chapters.forEach((chapter) => {
              const topic = chapter.name;
              initialLevels[subject][topic] = {
                level: null,
                lastUpdated: null,
                subtopics: {},
              };

              // Process subtopics for this chapter
              if (chapter.subtopics && Array.isArray(chapter.subtopics)) {
                chapter.subtopics.forEach((subtopic) => {
                  initialLevels[subject][topic].subtopics[subtopic.name] = {
                    level: null,
                    lastUpdated: null,
                  };
                });
              }
            });
          }
        });

        setSubjectLevels((prevLevels) => mergeLevels(initialLevels, prevLevels));
        setSubjectList((prevList) => {
          const nextList = [...subjectsFromSyllabus];
          prevList.forEach((prevSubject) => {
            if (!nextList.includes(prevSubject)) {
              nextList.push(prevSubject);
            }
          });
          return nextList;
        });
        setIsLoadingSyllabus(false);
        setIsLoading(false);
      } catch (err) {
        console.error("Error fetching syllabus:", err);
        setSyllabusError(err.message);
        setSyllabusData([]);
      } finally {
        setIsLoadingSyllabus(false);
      }
    };
    fetchSyllabus();
  }, []);

  useEffect(() => {
    if (!subjectList.length) return;

    setSelectedSubject((prevSelected) => {
      if (prevSelected && subjectList.includes(prevSelected)) {
        return prevSelected;
      }

      const searchParams = new URLSearchParams(location.search);
      const requestedSubject = searchParams.get("subject");

      if (requestedSubject) {
        const matchedSubject = subjectList.find(
          (subject) =>
            subject?.toLowerCase() === requestedSubject.toLowerCase()
        );
        if (matchedSubject) {
          return matchedSubject;
        }
      }

      return subjectList[0];
    });
  }, [subjectList, location.search]);

  // // Process syllabus data when it changes
  // useEffect(() => {
  //   if (
  //     syllabusData &&
  //     Array.isArray(syllabusData) &&
  //     syllabusData.length > 0
  //   ) {
  //     const initialLevels = {};
  //     const initialOpenSections = {};

  //     // Process the syllabus data array
  //     const sortedSyllabus = [...syllabusData].sort((a, b) => {
  //       const rankDifference =
  //         getSubjectRank(a.subject) - getSubjectRank(b.subject);
  //       if (rankDifference !== 0) return rankDifference;
  //       return (a?.subject || "").localeCompare(b?.subject || "");
  //     });

  //     sortedSyllabus.forEach((syllabusItem) => {
  //       const subject = syllabusItem.subject;
  //       initialLevels[subject] = {};
  //       initialOpenSections[subject] = true;

  //       // Process chapters/topics for this subject
  //       if (syllabusItem.chapters && Array.isArray(syllabusItem.chapters)) {
  //         syllabusItem.chapters.forEach((chapter) => {
  //           const topic = chapter.name;
  //           initialLevels[subject][topic] = { level: null };

  //           // Process subtopics for this chapter
  //           if (chapter.subtopics && Array.isArray(chapter.subtopics)) {
  //             initialLevels[subject][topic].subtopics = {};
  //             chapter.subtopics.forEach((subtopic) => {
  //               initialLevels[subject][topic].subtopics[subtopic.name] = {
  //                 level: null,
  //               };
  //             });
  //           }
  //         });
  //       }
  //     });

  //     setSubjectLevels(initialLevels);
  //     setOpenSections(initialOpenSections);
  //     setIsLoading(false);
  //   } else if (syllabusError) {
  //     console.error("Error loading syllabus:", syllabusError);
  //     toast.error("Failed to load syllabus.");
  //     setIsLoading(false);
  //   } else if (!isLoadingSyllabus) {
  //     // Syllabus loading is complete but no data
  //     setIsLoading(false);
  //   }
  // }, [syllabusData, syllabusError, isLoadingSyllabus]);

  useEffect(() => {
    const fetchAssessment = async () => {
      console.log("Fetching saved self-assessment levels from useffect...");
      if (!isLoading) {
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const response = await axios.get(`${baseUrl}/v2/self-assessment`, {
              headers: { Authorization: `Bearer ${token}` },
            });

            if (response.data?.levels) {
              const serverLevels = response.data.levels;
              const serverSubjects = Object.keys(serverLevels);

              if (serverSubjects.length > 0) {
                setSubjectList((prevList) => {
                  const updatedList = [...prevList];
                  let hasChanges = false;

                  serverSubjects.forEach((subject) => {
                    if (!updatedList.includes(subject)) {
                      updatedList.push(subject);
                      hasChanges = true;
                    }
                  });

                  return hasChanges ? updatedList : prevList;
                });
              }

              setSubjectLevels((prevLevels) =>
                mergeLevels(prevLevels, serverLevels)
              );
            }
          } catch (error) {
            console.error("Error fetching saved levels:", error);
            toast.error("Failed to load previous assessment.");
          }
        }
      }
    };

    fetchAssessment();
  }, [isLoading, baseUrl]);

  const handleSubtopicLevelSelect = (subject, topic, subtopic, level) => {
    const timestamp = new Date().toISOString();
    setSubjectLevels((prevLevels) => {
      const updatedLevels = {
        ...prevLevels,
        [subject]: {
          ...prevLevels[subject],
          [topic]: {
            ...prevLevels[subject][topic],
            subtopics: {
              ...prevLevels[subject][topic]?.subtopics,
              [subtopic]: {
                ...(prevLevels[subject][topic]?.subtopics?.[subtopic] || {
                  level: null,
                  lastUpdated: null,
                }),
                level,
                lastUpdated: timestamp,
              },
            },
          },
        },
      };
      const calculatedLevel = calculateTopicLevel(
        updatedLevels[subject][topic].subtopics
      );
      if (calculatedLevel) {
        updatedLevels[subject][topic].level = calculatedLevel;
      }
      updatedLevels[subject][topic].lastUpdated = timestamp;
      return updatedLevels;
    });
  };

  // New function to handle chapter level clicks with enhanced logic
  const handleChapterLevelClick = (subject, topic, level) => {
    const timestamp = new Date().toISOString();
    setSubjectLevels((prevLevels) => {
      if (!prevLevels[subject]?.[topic]) {
        return prevLevels;
      }

      const updatedLevels = {
        ...prevLevels,
        [subject]: {
          ...prevLevels[subject],
        },
      };

      const topicData = prevLevels[subject][topic];
      const hasSubtopics =
        topicData.subtopics && Object.keys(topicData.subtopics).length > 0;

      if (hasSubtopics) {
        const updatedSubtopics = { ...topicData.subtopics };

        Object.keys(updatedSubtopics).forEach((subtopicName) => {
          const existingSubtopic = updatedSubtopics[subtopicName] || {
            level: null,
            lastUpdated: null,
          };

          if (!existingSubtopic.level) {
            updatedSubtopics[subtopicName] = {
              ...existingSubtopic,
              level,
              lastUpdated: timestamp,
            };
          }
        });

        const recalculatedLevel = calculateTopicLevel(updatedSubtopics);

        updatedLevels[subject][topic] = {
          ...topicData,
          level: recalculatedLevel || level,
          lastUpdated: timestamp,
          subtopics: updatedSubtopics,
        };
      } else {
        updatedLevels[subject][topic] = {
          ...topicData,
          level,
          lastUpdated: timestamp,
        };
      }

      return updatedLevels;
    });
  };

  const getTopicLevel = (subject, topic) => {
    return subjectLevels[subject]?.[topic]?.level || null;
  };

  const getSubtopicLevel = (subject, topic, subtopic) => {
    return (
      subjectLevels[subject]?.[topic]?.subtopics?.[subtopic]?.level || null
    );
  };

  const handleSubjectTabClick = (subject) => {
    setSelectedSubject(subject);
  };

  const handleContinue = async () => {
    const levelsPayload = JSON.parse(JSON.stringify(subjectLevels));

    try {
      const token = localStorage.getItem("token");
      const userId = localStorage.getItem("userId");
      const headers = {};

      if (token) {
        headers["Authorization"] = `Bearer ${token}`;
      } else if (userId) {
        headers["X-User-ID"] = userId;
        console.log("Sending User ID for self-assessment:", userId);
      } else {
        console.warn(
          "No token or User ID found. Self-assessment data will not be associated."
        );
        toast.error("Please log in to save your assessment.");
        return;
      }

      const response = await axios.post(
        `${baseUrl}/v2/self-assessment`,
        { levels: levelsPayload },
        { headers: headers }
      );

      console.log("Data saved successfully:", response.data);
      toast.success("Assessment saved successfully!");
      setTimeout(() => {
        navigate("/");
      }, 1000);
    } catch (error) {
      console.error("Error saving data:", error);
      toast.error("Failed to save assessment. Please try again.");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#0D99FF] mb-4"></div>
          <p className="text-gray-600 text-lg">
            Loading self-assessment data...
          </p>
        </div>
      </div>
    );
  }

  const tabSubjects =
    subjectList.length > 0
      ? subjectList
      : Object.keys(subjectLevels ?? {});
  const shouldDisplaySelectedSubject =
    selectedSubject && subjectLevels[selectedSubject];
  const subjectsToRender = shouldDisplaySelectedSubject
    ? [selectedSubject]
    : tabSubjects;

  return (
    <div className="self-assessment-page min-h-screen bg-gray-50">
      {/* Top Banner */}
      <div className="rounded-t-md bg-[#0D99FF] text-white p-6 flex justify-between items-center mb-5">
        <div className="flex items-center gap-4">
          <button
            onClick={handleBackClick}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
            aria-label="Go back"
          >
            <FaArrowLeft className="text-lg" />
            <span className="text-sm font-medium">Back</span>
          </button>
          <div>
            <h1 className="text-2xl font-bold">
              Mark your level as per your understanding.
            </h1>
            {/* <p className="text-base mt-1">
              How would you like to show your current skills?
            </p> */}
          </div>
        </div>
        <img src="/assets/images/robot.png" alt="Robot" className="w-12 h-12" />
      </div>

      {/* Tabs */}
      <div className="px-6 mb-8">
        {/* <div className="flex gap-3">
          <button
            onClick={() => setActiveTab("self-assessment")}
            className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
              activeTab === "self-assessment"
                ? "bg-gradient-to-r from-[#0D99FF] to-[#9F7AEA] text-white shadow-lg shadow-[#0D99FF]/25"
                : "bg-white text-gray-600 hover:text-[#0D99FF] border-2 border-gray-200 hover:border-[#0D99FF]/30 hover:shadow-md"
            }`}
          >
            Self Assessment
          </button>
          <button
            onClick={() => handleTabClick("evaluate-answer-sheets")}
            className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
              activeTab === "evaluate-answer-sheets"
                ? "bg-gradient-to-r from-[#0D99FF] to-[#9F7AEA] text-white shadow-lg shadow-[#0D99FF]/25"
                : "bg-white text-gray-600 hover:text-[#0D99FF] border-2 border-gray-200 hover:border-[#0D99FF]/30 hover:shadow-md"
            }`}
          >
            Evaluate Answer Sheets
          </button>
          <button
            onClick={() => handleTabClick("take-challenge")}
            className={`flex-1 py-4 px-6 rounded-xl text-sm font-semibold transition-all duration-300 transform hover:scale-105 ${
              activeTab === "take-challenge"
                ? "bg-gradient-to-r from-[#0D99FF] to-[#9F7AEA] text-white shadow-lg shadow-[#0D99FF]/25"
                : "bg-white text-gray-600 hover:text-[#0D99FF] border-2 border-gray-200 hover:border-[#0D99FF]/30 hover:shadow-md"
            }`}
          >
            Take Aanya Challenge
          </button>
        </div> */}
      </div>

      {/* Instruction text for self assessment */}
      {activeTab === "self-assessment" && (
        <div className="px-6 mb-3">
          {/* <p className="text-black text-left text-sm">
            Please mark your level across chapter/topic as per your
            understanding.
          </p> */}
        </div>
      )}

      {/* <Toaster position="top-center" reverseOrder={false} /> */}
      {tabSubjects.length > 0 && (
        <div className="px-6 mb-6 flex justify-center">
          <div className="flex gap-2 md:gap-3 overflow-x-auto -mx-2 px-2 md:mx-0 md:px-0 justify-center items-center bg-gray-100 rounded-full">
            {tabSubjects.map((subject) => (
              <button
                key={subject}
                onClick={() => handleSubjectTabClick(subject)}
                className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                  selectedSubject === subject
                    ? "bg-[#0D99FF] text-white shadow-md"
                    : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}
              >
                {subject}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="w-full px-6 md:px-20">
        {subjectsToRender.length === 0 ? (
          <div className="text-center text-gray-500 py-10">
            No subjects available for self-assessment.
          </div>
        ) : (
          subjectsToRender.map((subject) => {
            const topicEntries = Object.entries(subjectLevels[subject] || {});

            return (
              <div key={subject} className="mb-10">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex flex-wrap items-center justify-between gap-3">
                    <div>
                      <h2 className="text-xl font-semibold text-gray-800">
                        {subject}
                      </h2>
                      <p className="text-sm text-gray-500">
                        Mark your level for each chapter and subtopic.
                      </p>
                    </div>
                    <span className="text-sm text-gray-500">
                      {topicEntries.length}{" "}
                      {topicEntries.length === 1 ? "chapter" : "chapters"}
                    </span>
                  </div>
                  <div className="p-4 md:p-6 space-y-4">
                    {topicEntries.map(([topic, details], index) => {
                      const subtopicEntries = Object.entries(
                        details.subtopics || {}
                      );
                      const hasSubtopics = subtopicEntries.length > 0;
                      const hasSubtopicLevels = subtopicEntries.some(
                        ([, subtopicDetails]) => !!subtopicDetails?.level
                      );

                      return (
                        <div
                          key={topic}
                          className="bg-gray-50 border border-gray-100 rounded-xl p-4 md:p-5 space-y-4"
                        >
                          <TopicLevelSelector
                            number={index + 1}
                            topic={topic}
                            selectedLevel={getTopicLevel(subject, topic)}
                            onLevelSelect={(newLevel) =>
                              handleChapterLevelClick(subject, topic, newLevel)
                            }
                            hasSubtopics={hasSubtopics}
                            isSelectionLocked={hasSubtopics && hasSubtopicLevels}
                          />

                          {hasSubtopics && (
                            <div className="space-y-3">
                              {subtopicEntries.map(([subtopic], subIndex) => (
                                <SubtopicLevelSelector
                                  key={subtopic}
                                  number={`${index + 1}.${subIndex + 1}`}
                                  subtopic={subtopic}
                                  selectedLevel={getSubtopicLevel(
                                    subject,
                                    topic,
                                    subtopic
                                  )}
                                  onLevelSelect={(newLevel) =>
                                    handleSubtopicLevelSelect(
                                      subject,
                                      topic,
                                      subtopic,
                                      newLevel
                                    )
                                  }
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })
        )}

        <div className="mt-8 flex justify-center gap-4">
          <Button className="px-20" onClick={handleContinue}>
            Continue
          </Button>
          <Button variant="secondary" onClick={() => navigate("/")}>
            Skip for Now
          </Button>
        </div>
      </div>
      <Outlet />
    </div>
  );
};

const TopicLevelSelector = ({
  number,
  topic,
  selectedLevel,
  onLevelSelect,
  hasSubtopics,
  isSelectionLocked,
}) => {
  const levels = [
    "Beginner",
    "Developing",
    "Proficient",
    "Advanced",
    "Mastery",
  ];

  const handleLevelClick = (level) => {
    if (isSelectionLocked) {
      alert(
        "Chapter level is determined by subtopic levels. Adjust individual subtopics to change the chapter level."
      );
      return;
    }
    onLevelSelect(level);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center">
            <span className="text-base font-semibold">{number}</span>
          </div>
          <div>
            <p className="text-gray-800 font-semibold">{topic}</p>
            {hasSubtopics && (
              <p className="text-xs text-gray-500">
                {isSelectionLocked
                  ? "Level set by individual subtopics"
                  : "Apply a level to all subtopics"}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="flex gap-2 flex-wrap">
        {levels.map((level) => (
          <button
            key={level}
            className={`level-button ${
              selectedLevel === level
                ? "level-button-active"
                : "level-button-inactive"
            } ${isSelectionLocked ? "opacity-50 cursor-not-allowed" : ""}`}
            onClick={() => handleLevelClick(level)}
            disabled={isSelectionLocked}
            title={
              isSelectionLocked
                ? "Determined by subtopic levels"
                : "Select level"
            }
          >
            {level}
          </button>
        ))}
      </div>
    </div>
  );
};

const SubtopicLevelSelector = ({
  number,
  subtopic,
  selectedLevel,
  onLevelSelect,
}) => {
  const levels = [
    "Beginner",
    "Developing",
    "Proficient",
    "Advanced",
    "Mastery",
  ];

  return (
    <div className="flex flex-wrap items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors duration-200 bg-white border border-gray-100 shadow-sm">
      <div className="w-6 h-6 rounded-full bg-gray-200 text-gray-700 flex items-center justify-center">
        <span className="text-xs font-semibold">{number}</span>
      </div>
      <div className="flex-1 min-w-[180px]">
        <span className="text-gray-700 font-medium">{subtopic}</span>
      </div>
      <div className="flex gap-2 flex-wrap">
        {levels.map((level) => (
          <button
            key={level}
            className={`level-button text-xs ${
              selectedLevel === level
                ? "level-button-active"
                : "level-button-inactive"
            }`}
            onClick={() => onLevelSelect(level)}
          >
            {level}
          </button>
        ))}
      </div>
    </div>
  );
};

export default SelfAssessmentPage;
